import {
  NumThrulanes,
  CrashSeverity,
  AreaType,
  AADTs,
  SignalizedRampTerminal,
  StopControlRampTerminal,
  RoundaboutRampSpfType,
} from "../SPFConstants";
import {
  get3LegRoundaboutSPFs,
  getFatalInjuryCrashRatio,
  getaadtxrd,
} from "../spfHelpers";

const FI_COEFFICIENTS = { b: 1.191, c: 0.001, d: 0.131 };
const PDO_COEFFICIENTS = { b: 0.879, c: 0.001, d: 0.545 };

interface SPFCoefficients {
  a: number;
  b: number;
  c: number;
  d: number;
}

const getSpfs = (
  { aadten, aadtex, aadtin, aadtout }: AADTs,
  { a, b, c, d }: SPFCoefficients
) => {
  const aadtxrd = getaadtxrd({ aadtin, aadtout });
  return Math.exp(
    a + b * Math.log(c * aadtxrd) + d * Math.log(c * aadtex + c * aadten)
  );
};

const getSPFCoefficientsSignalized = (
  crashSeverity: CrashSeverity.FATAL_INJURY | CrashSeverity.PDO,
  numThruLanes: NumThrulanes
): SPFCoefficients => {
  const fiMap = new Map<NumThrulanes, SPFCoefficients>();
  fiMap.set(2, { a: -2.655, ...FI_COEFFICIENTS });
  fiMap.set(3, { a: -2.495, ...FI_COEFFICIENTS });
  fiMap.set(4, { a: -2.335, ...FI_COEFFICIENTS });
  fiMap.set(5, { a: -2.175, ...FI_COEFFICIENTS });
  fiMap.set(6, { a: -2.015, ...FI_COEFFICIENTS });

  const pdoMap = new Map<NumThrulanes, SPFCoefficients>();
  pdoMap.set(2, { a: -2.248, ...PDO_COEFFICIENTS });
  pdoMap.set(3, { a: -2.16, ...PDO_COEFFICIENTS });
  pdoMap.set(4, { a: -2.072, ...PDO_COEFFICIENTS });
  pdoMap.set(5, { a: -2.985, ...PDO_COEFFICIENTS });
  pdoMap.set(6, { a: -1.897, ...PDO_COEFFICIENTS });

  const spfCoefficients =
    crashSeverity === CrashSeverity.FATAL_INJURY
      ? fiMap.get(numThruLanes)
      : pdoMap.get(numThruLanes);

  if (!spfCoefficients) {
    throw new Error(
      `Cannot find spf coefficients for ${crashSeverity} num thru lanes: ${numThruLanes}`
    );
  }

  return spfCoefficients;
};

export const getSpfForSignalizedRampterminal = (
  props: SignalizedRampTerminal
) => {
  const numThrulanes =
    props.areaType === AreaType.RURAL && props.bothApproachesNumThruLanes > 4
      ? 4
      : props.bothApproachesNumThruLanes;
  return getSpfs(
    props,
    getSPFCoefficientsSignalized(props.crashSeverity, numThrulanes)
  );
};

const getSPFCoefficientsForOneWaySC = (
  crashSeverity: CrashSeverity.FATAL_INJURY | CrashSeverity.PDO,
  areaType: AreaType
): { a: number; b: number; c: number; d: number } => {
  if (crashSeverity === CrashSeverity.FATAL_INJURY) {
    const common = { b: 1.008, c: 0.001, d: 0.177 };
    return areaType === AreaType.RURAL
      ? { ...common, a: -2.74 }
      : { ...common, a: -3.064 };
  }
  if (crashSeverity === CrashSeverity.PDO) {
    return { a: -2.432, b: 0.845, c: 0.001, d: 0.476 };
  }

  throw new Error(
    `Cannot find SPF coefficients for ${crashSeverity} ${areaType}`
  );
};

export const getSpfForOneWaySCRampterminal = (
  props: StopControlRampTerminal
) => {
  const spfCoefficients = getSPFCoefficientsForOneWaySC(
    props.crashSeverity,
    props.areaType
  );
  return getSpfs(props, spfCoefficients);
};

export const getSpfForRoundaboutRampTerminal = ({
  aadtin,
  aadtout,
  // aadtex, ignore exit ramp AADT since we are using a 3 leg SPF. Exit ramp is the missing leg
  aadten,
  areaType,
  crashSeverity,
  totalCrashes,
}: RoundaboutRampSpfType & { totalCrashes?: number }): number => {
  const { getFatalInjuryPredicted, getPDOPredicted, getTotalPredicted } =
    get3LegRoundaboutSPFs(areaType);
  const aadts = {
    majorRoad: { aadt: Math.max(aadtin, aadtout) },
    minorRoad: { aadt: aadten },
  };

  if (crashSeverity === CrashSeverity.FATAL_INJURY) {
    return getFatalInjuryCrashRatio({
      fiInitial: getFatalInjuryPredicted(aadts),
      pdoInitial: getPDOPredicted(aadts),
      totalCrashes: totalCrashes ?? getTotalPredicted(aadts),
    });
  }

  return getTotalPredicted(aadts);
};
